import clsx from 'clsx'
import {compact, find, get, isEmpty, isNull, reduce, size} from 'lodash'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {useLocation, useParams, useSearchParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {CSSTransition} from 'react-transition-group'
import {ConfirmActionModal, KTSVG} from '../../../../../gori/components'
import {SkeletonA1BatchDetail} from '../../../../../gori/components/skeleton/SkeletonA1BatchDetail'
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  EXCEL_EXPORT_OPTIONS,
  FileExtension,
  FORMAT_DATE,
  OPTION_COUNTRIES,
  OrderStatus,
  TABLE_KEY_COLUMN,
} from '../../../../../gori/constants'
import {convertUserTimeZone, setLoadingActionTable} from '../../../../../gori/helpers'
import {useCancelToken, UsePermission} from '../../../../../gori/hooks'
import {PageTitle} from '../../../../../gori/layout'
import {
  useA1BatchesProvider,
  useAuth,
  useDisabled,
  useStatesGlobal,
} from '../../../../../gori/providers'
import {
  Button,
  ButtonBack,
  DataTab,
  DropdownButton,
  Table,
  TableBody,
  TableHeader,
  TableTabs,
  TableWrapper,
  ValidationErrorModal,
} from '../../../../../gori/widgets'
import {
  A1_CONSTANTS,
  A1BatchesDetailFilter,
  A1BatchRenameModal,
  A1BatchService,
  A1Helpers,
  ConfirmA1BatchModal,
  EditOrderInfo,
  JourneyInfoCard,
  JourneyInformationModal,
  JourneyStatusCard,
  MassUpdateModal,
  ReasonModal,
  RemovedOrdersHistoryModal,
  T86DocumentModal,
  TicketForumModal,
} from '../../../a1'
import {getCSSVariableValue} from '../../../../../gori/assets/ts/_utils'
import {useA1OrderFilter, useConfirmModal} from '../../hooks'
import {UpdateStatus} from '../../modals/batchesDetail/UpdateStatus'
import {StatusViewOnly} from '../../modals/batchesDetail/StatusViewOnly'
import {TooltipModal} from '../../modals/batchesDetail/TooltipModal'
import {OrderTrackingNumberCell} from '../../modals/batchesDetail/order/OrderTrackingNumberCell'
import {TrackingNumbersDetail} from '../../modals/batchesDetail/order/TrackingNumbersDetail'
import {LABEL_CREATED_STATUS} from '../../definitions'
import {CreateLabelButton} from './CreateLabelButton'
import {PrintLabelButton} from './PrintLabelButton'
import {CUSTOM_ISSUES_TAB_KEY} from '../../definitions'

const AddressView = ({
  data,
  prefix = null,
  classNames = null,
}: {
  data: any
  prefix?: string | null
  classNames?: string | null
}) => {
  const company = get(data, prefix ? `${prefix}_company` : 'company')
  const firstName = get(data, prefix ? `${prefix}_first_name` : 'first_name', '')
  const lastName = get(data, prefix ? `${prefix}_last_name` : 'last_name', '')
  const fullName = compact([firstName, lastName]).join(' ')
  const city = get(data, prefix ? `${prefix}_city` : 'city', '')
  const state = get(data, prefix ? `${prefix}_state` : 'state', '')
  const location = compact([city, state]).join(', ')

  return (
    <span>
      <p className={clsx('m-0', classNames)}>{isEmpty(company) ? fullName : company}</p>
      <p className='m-0 text-gray-600'>{location}</p>
    </span>
  )
}

const A1BatchesDetailPage: React.FC = () => {
  const intl = useIntl()
  const {id = ''} = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const {setPageDisabled} = useDisabled()
  const {currentUser, isUserRole} = useAuth()
  const {isOrigin, isDestination} = UsePermission()
  const {newCancelToken, isCancel} = useCancelToken()
  const {confirm, ConfirmModal} = useConfirmModal()
  const {
    a1Batch,
    setA1Batch,
    ticketForum,
    setTicketForum,
    reloadTableDetail,
    setCustomPackageTypes,
  } = useA1BatchesProvider()
  const [tableData, setTableData] = useState<any>([])
  const [tableDataAll, setTableDataAll] = useState<any>([])
  const [pagination, setPagination] = useState<any>({})
  const [validationErrors, setValidationErrors] = useState<any>()
  const [selectedListId, setSelectedListId] = useState<any>([])
  const [selectedMaster, setSelectedMaster] = useState<boolean>(false)

  // VNP users, plus every partner on a Yamato batch, can create/print/finalize labels.
  const canUseLabelFeatures = isUserRole || !!a1Batch?.is_yamato

  const [loading, setLoading] = useState<{
    first: boolean
    getA1Batch: boolean
    markAsPickedUp: boolean
    setDropOff: boolean
    markAsDroppedOff: boolean
    notifyDestination: boolean
    massAsShipped: boolean
    exportManifest: boolean
    finalizeLabels: boolean
  }>({
    first: true,
    getA1Batch: false,
    markAsPickedUp: false,
    setDropOff: false,
    markAsDroppedOff: false,
    notifyDestination: false,
    massAsShipped: false,
    exportManifest: false,
    finalizeLabels: false,
  })
  const [showModal, setShowModal] = useState<{
    rename: boolean
    cancelBatch: boolean
    confirmPickUpdate: boolean
    journeyInfo: boolean
    editOrderInfo: boolean
    t86DocumentModal: boolean
    confirmJourneyStatus: boolean
    massUpdate: boolean
    reason: boolean
    trackingNumbersDetail: boolean
    removedOrdersHistory: boolean
  }>({
    rename: false,
    cancelBatch: false,
    confirmPickUpdate: false,
    journeyInfo: false,
    editOrderInfo: false,
    t86DocumentModal: false,
    confirmJourneyStatus: false,
    massUpdate: false,
    reason: false,
    trackingNumbersDetail: false,
    removedOrdersHistory: false,
  })
  const [dataModal, setDataModal] = useState<{
    editOrderInfo: any
    confirmJourneyStatus: {
      title?: string | null
      message?: any
      callback: any
    }
    massUpdate: {
      selectAll: boolean
      selectedList: any
      typeMassUpdate: string
      releaseStatus?: string
      rowId?: number
    } | null
    reasonData: {type: string; reason: string; first_release_status: string} | null
    trackingNumbersDetail: any
  }>({
    editOrderInfo: {},
    confirmJourneyStatus: {title: null, message: null, callback: null},
    massUpdate: null,
    reasonData: null,
    trackingNumbersDetail: [],
  })
  const {state} = useLocation()

  const [dataTabs, setDataTabs] = useState<DataTab[]>([])
  const [updatingStatus, setUpdatingStatus] = useState<{[key: string]: boolean}>({})
  const {statesGlobal} = useStatesGlobal()

  const {filters} = useA1OrderFilter()

  const {
    READY,
    PICK_UP_DROP_OFF,
    AIR_DELIVERY,
    CUSTOMS,
    TRANSFERRED_TO_LOCAL_CARRIER,
    DELIVERY_STATUS,
  } = A1_CONSTANTS.OPTIONS_JOURNEY_STATUS

  const carriers = statesGlobal.settingsConfig?.carriers

  const isCustomIssuesTab = useMemo(
    () => searchParams.get('order_tab') === CUSTOM_ISSUES_TAB_KEY,
    [searchParams]
  )

  const setTable = useCallback(async () => {
    try {
      setPageDisabled(true)
      const config = {
        params: {
          batch_id: id,
          page: searchParams.get('page') || DEFAULT_PAGE,
          page_size: searchParams.get('page_size') || DEFAULT_PAGE_SIZE,
          first_held: searchParams.get('first_held') || null,
          status: searchParams.get('status') || null,
          sort_by: searchParams.get('sort_by') || null,
          sort_direction: searchParams.get('sort_direction') || null,
          order_tab: searchParams.get('order_tab') || undefined,
          ...filters,
        },
        cancelToken: newCancelToken(),
      }
      const response: any = await A1BatchService.getA1BatchOrders(config)
      setDataTabs(response.statistics)

      if (searchParams.get('order_tab') === CUSTOM_ISSUES_TAB_KEY) {
        const customIssuesTab = (response.statistics || []).find(
          (tab: DataTab) => tab.value === CUSTOM_ISSUES_TAB_KEY
        )
        if (!customIssuesTab?.count) {
          searchParams.delete('order_tab')
          searchParams.set('page', DEFAULT_PAGE)
          setSearchParams(searchParams)
          return
        }
      }

      if (response) {
        let newOrder = Array.from(response.orders, (order: any) => {
          return {
            ...order,
            canCheck: true,
          }
        })
        setTableData(newOrder)
        setTableDataAll(response.all)
        setPagination(response.pagination)
        setCustomPackageTypes(response?.packages || [])
      }
    } catch (error: any) {
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    } finally {
      setPageDisabled(false)
      setLoading((prev) => ({...prev, first: false}))
    }
  }, [
    filters,
    id,
    isCancel,
    newCancelToken,
    searchParams,
    setSearchParams,
    setCustomPackageTypes,
    setPageDisabled,
  ])

  const getA1Batch = useCallback(async () => {
    try {
      setLoading((prev) => ({...prev, getA1Batch: true}))
      const {data}: any = await A1BatchService.getDetail(id, {cancelToken: newCancelToken()})
      if (data) {
        setA1Batch(data)
      }
    } catch (error: any) {
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    } finally {
      setLoading((prev) => ({...prev, getA1Batch: false}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCancel, newCancelToken])

  useEffect(() => {
    setTable()

    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setTable, reloadTableDetail])

  useEffect(() => {
    getA1Batch()

    return () => {
      setA1Batch(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTableDetail])

  const baseColor = getCSSVariableValue('--bs-primary')

  const showEditOrderInfo = useCallback(
    (child, row, showRequired = false) => {
      return (
        <div
          className='cursor-pointer'
          onClick={() => {
            setDataModal((prev) => ({...prev, editOrderInfo: row.original}))
            setShowModal((prev) => ({...prev, editOrderInfo: true}))
          }}
        >
          {(showRequired && (
            <div className='d-flex text-danger'>
              <KTSVG path='/media/gori/common/invalid.svg' className='svg-icon-3 text-danger' />
              <span className='ms-2 text-decoration-underline text-uppercase'>
                {intl.formatMessage({id: 'REQUIRED'})}
              </span>
            </div>
          )) ||
          child ? (
            <span className='text-hover-primary'>{child}</span>
          ) : null}
        </div>
      )
    },
    [intl]
  )

  const onSelectStatus = useCallback(
    (option, row: any) => {
      if (option.value === row.original.release_status) return

      setShowModal((prev) => ({...prev, massUpdate: true}))
      setDataModal((prev) => ({
        ...prev,
        massUpdate: {
          selectAll: false,
          selectedList: {
            selected: [row],
            total: tableDataAll,
          },
          typeMassUpdate: option.typeMassUpdate,
          releaseStatus: option.releaseStatus,
          rowId: row.original.id,
        },
      }))
    },
    [tableDataAll]
  )

  const isHoldOrRejected = useCallback((status) => {
    return [
      A1_CONSTANTS.OPTIONS_RELEASE_STATUS.HOLD.value,
      A1_CONSTANTS.OPTIONS_RELEASE_STATUS.REJECTED.value,
    ].includes(status)
  }, [])

  const isVisibleCreateLabelButton = useMemo(() => {
    if (isDestination || !canUseLabelFeatures) return false

    return (
      a1Batch?.status === A1_CONSTANTS.A1_BATCH_STATUS.OPEN &&
      a1Batch?.label_created !== LABEL_CREATED_STATUS.FULL
    )
  }, [isDestination, canUseLabelFeatures, a1Batch?.status, a1Batch?.label_created])

  const isVisiblePrintLabelsButton = useMemo(() => {
    if (isDestination || !canUseLabelFeatures) return false

    return !isNull(a1Batch?.label_created)
  }, [isDestination, canUseLabelFeatures, a1Batch?.label_created])

  const isVisibleFinalizeBatchButton = useMemo(() => {
    if (isDestination || !canUseLabelFeatures) return false

    return (
      a1Batch?.status === A1_CONSTANTS.A1_BATCH_STATUS.OPEN &&
      a1Batch?.label_created === LABEL_CREATED_STATUS.FULL
    )
  }, [isDestination, canUseLabelFeatures, a1Batch?.status, a1Batch?.label_created])

  const isVisibleCheckbox = useMemo(
    () => isDestination || isVisibleCreateLabelButton || isVisiblePrintLabelsButton,
    [isDestination, isVisibleCreateLabelButton, isVisiblePrintLabelsButton]
  )

  const columns = useMemo(() => {
    const _columns = [
      {
        id: 'created_at',
        Header: intl.formatMessage({id: 'DATE'}),
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => {
          return showEditOrderInfo(
            row.original?.created_at &&
              convertUserTimeZone(row.original?.created_at, currentUser, FORMAT_DATE.DATE),
            row
          )
        },
      },
      {
        id: 'code',
        Header: 'BAE ID',
        headerClassName: 'min-w-150px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => showEditOrderInfo(row.original.bae_id, row),
      },
      {
        id: 'customer',
        Header: intl.formatMessage({id: 'CUSTOMER'}),
        headerClassName: 'min-w-200px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => {
          return showEditOrderInfo(<AddressView data={row.original.order_ext} prefix='to' />, row)
        },
      },
      {
        id: 'tracking_number',
        Header: intl.formatMessage({id: 'TRACKING_NUMBER'}),
        headerClassName: 'min-w-150px d-flex flex-column align-items-center pe-6',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => {
          if (isEmpty(carriers)) return null
          let service = carriers[row.original.carrier + '_' + row.original.service] ?? []

          const isMultiParcel = row.original?.tracking_numbers.length > 1
          const trackingNumbers = row.original?.tracking_numbers || null
          return (
            <OrderTrackingNumberCell
              onClickTrackingNumbersDetail={() => {
                setShowModal((prev) => ({...prev, trackingNumbersDetail: true}))
                setDataModal((prev) => ({
                  ...prev,
                  trackingNumbersDetail: row,
                }))
              }}
              service={service}
              trackingNumbers={trackingNumbers}
              isMultiParcel={isMultiParcel}
            />
          )
        },
      },
      {
        id: 'hawb',
        Header: 'HAWB',
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => {
          return showEditOrderInfo(row?.original?.hawb, row)
        },
      },
      {
        id: 'container_id',
        Header: intl.formatMessage({id: 'CONTAINER_ID'}),
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => {
          return showEditOrderInfo(row?.original?.container_id, row)
        },
      },
      {
        id: 'name',
        Header: intl.formatMessage({id: 'NAME'}),
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) =>
          showEditOrderInfo(
            (row.original?.expand
              ? row.original?.order_items
              : [row.original?.order_items?.[0]]
            ).map((item: any) => {
              return (
                item?.name && (
                  <div className='d-flex'>
                    <span className='mw-200px text-truncate'>{item?.name}</span>
                  </div>
                )
              )
            }),
            row,
            isEmpty(row.original?.order_items) ||
              row.original?.order_items?.some((item) => isEmpty(item?.name))
          ),
        visible: !isCustomIssuesTab,
      },
      {
        id: 'release_status',
        Header: intl.formatMessage({id: 'CUSTOM_STATUS'}),
        headerClassName: 'min-w-150px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => {
          const foundRelease =
            find(Object.values(A1_CONSTANTS.OPTIONS_RELEASE_STATUS), {
              value: row?.original?.release_status,
            }) || null
          return showEditOrderInfo(
            foundRelease && intl.formatMessage({id: foundRelease?.label}),
            row
          )
        },
        visible: !isCustomIssuesTab,
      },
      {
        id: 'content',
        Header: intl.formatMessage({id: 'CONTENT'}),
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) =>
          !isEmpty(row.original?.order_items) && (
            <div>
              {(row.original?.expand
                ? row.original?.order_items
                : [row.original?.order_items[0]]
              ).map((item: any, idx) => {
                const content = find(A1_CONSTANTS.OPTIONS_CONTENT_TYPE, {value: item?.a1_content})
                return showEditOrderInfo(
                  content && (
                    <div className='d-flex' key={idx}>
                      <span className='mw-200px text-truncate'>
                        {intl.formatMessage({id: content?.label})}
                      </span>
                    </div>
                  ),
                  row
                )
              })}
            </div>
          ),
      },
      {
        id: 'description',
        Header: intl.formatMessage({id: 'DESCRIPTION'}),
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) =>
          showEditOrderInfo(
            (row.original?.expand ? row.original?.order_items : [row.original?.order_items[0]]).map(
              (item: any, idx) => {
                return (
                  <div className='d-flex' key={idx}>
                    <span className='mw-200px text-truncate'>{item?.a1_description}</span>
                  </div>
                )
              }
            ),
            row
          ),
      },
      {
        id: 'hts_code',
        Header: intl.formatMessage({id: 'HTS_CODE'}),
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) =>
          showEditOrderInfo(
            (row.original?.expand ? row.original?.order_items : [row.original?.order_items[0]]).map(
              (item: any, idx) => {
                return (
                  <div className='d-flex' key={idx}>
                    <span className='mw-200px text-truncate'>{item?.a1_hs_tariff_number}</span>
                  </div>
                )
              }
            ),
            row
          ),
      },
      {
        id: 'country',
        Header: intl.formatMessage({id: 'COUNTRY_OF_ORIGIN'}),
        headerClassName: 'min-w-150px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) =>
          showEditOrderInfo(
            (row.original?.expand ? row.original?.order_items : [row.original?.order_items[0]]).map(
              (item: any, idx) => {
                const country = find(OPTION_COUNTRIES, {value: item?.a1_country})
                return (
                  country && (
                    <div className='d-flex' key={idx}>
                      <span className='mw-200px text-truncate'>{country?.label}</span>
                    </div>
                  )
                )
              }
            ),
            row
          ),
      },
      {
        id: 'quantity',
        Header: intl.formatMessage({id: 'QUANTITY'}),
        headerClassName: 'min-w-150px text-center',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) =>
          showEditOrderInfo(
            (row.original?.expand ? row.original?.order_items : [row.original?.order_items[0]]).map(
              (item: any, idx) => {
                return (
                  <div className='d-flex justify-content-center' key={idx}>
                    <span className='mw-200px text-truncate'>{item?.a1_quantity}</span>
                  </div>
                )
              }
            ),
            row
          ),
      },
      {
        id: 'value',
        Header: intl.formatMessage({id: 'VALUES_$'}),
        headerClassName: 'min-w-150px text-center',
        cellClassName: 'text-center text-gray-800',
        Cell: ({row}: {row: any}) =>
          showEditOrderInfo(
            !isEmpty(row.original?.order_items) && (
              <div className='d-flex flex-column align-items-center'>
                {(row.original?.expand
                  ? row.original?.order_items
                  : [row.original?.order_items[0]]
                ).map((item: any, idx) => {
                  const formatNumber = (number = 0) => {
                    return intl.formatNumber(Number(number), {
                      style: 'decimal',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }

                  return (
                    <div className='d-flex' key={idx}>
                      <span className='mw-200px text-truncate'>{formatNumber(item?.a1_value)}</span>
                    </div>
                  )
                })}
              </div>
            ),
            row
          ),
      },
      {
        id: 'action',
        Header: '',
        headerClassName: 'text-center',
        cellClassName: 'text-center',
        Cell: ({row}: {row: any}) => (
          <div
            className={clsx(
              'text-primary text-hover-active-primary fw-bold cursor-pointer text-decoration-underline',
              {
                'd-none': size(row.original.order_items) <= 1,
                'min-w-150px': size(row.original.order_items) > 1,
              }
            )}
            onClick={() => {
              setTableData(
                setLoadingActionTable(tableData, [row.original.id], !row.original.expand, 'expand')
              )
            }}
          >
            {intl.formatMessage({id: row.original.expand ? 'COLLAPSE' : 'VIEW_MORE_ITEMS'})}
          </div>
        ),
        visible: !isCustomIssuesTab,
      },
      {
        id: 'type',
        Header: intl.formatMessage({id: 'TYPE'}),
        headerClassName: 'min-w-175px',
        cellClassName: 'text-wrap text-dark',
        Cell: ({row}: {row: any}) => {
          if (!isHoldOrRejected(row.original.release_status)) return null

          const type =
            row.original.release_status === A1_CONSTANTS.OPTIONS_RELEASE_STATUS.REJECTED.value
              ? row?.original?.rejected_type
              : row?.original?.hold_type

          const foundType =
            find(statesGlobal.settingsConfig?.[row?.original?.first_release_status], {
              value: type,
            }) || null

          return foundType?.label || null
        },
        visible: isCustomIssuesTab,
      },
      {
        id: 'reason',
        Header: intl.formatMessage({id: 'REASON'}),
        headerClassName: 'min-w-100px text-center',
        cellClassName: 'text-wrap text-dark text-center',
        Cell: ({row}: {row: any}) => {
          if (!isHoldOrRejected(row.original.release_status)) return null

          const type =
            row.original.release_status === A1_CONSTANTS.OPTIONS_RELEASE_STATUS.REJECTED.value
              ? row?.original?.rejected_type
              : row?.original?.hold_type
          const reason =
            row.original.release_status === A1_CONSTANTS.OPTIONS_RELEASE_STATUS.REJECTED.value
              ? row?.original?.rejected_reason
              : row?.original?.hold_reason

          const foundType =
            find(statesGlobal.settingsConfig?.[row?.original?.first_release_status], {
              value: type,
            }) || null

          return (
            <TooltipModal
              message={
                <div
                  className='cursor-pointer'
                  onClick={() => {
                    setDataModal((prev) => ({
                      ...prev,
                      reasonData: {
                        type: foundType?.label || '',
                        reason,
                        first_release_status: row?.original?.first_release_status,
                      },
                    }))
                    setShowModal((prev) => ({...prev, reason: true}))
                  }}
                >
                  <div className='d-flex align-items-center text-danger'>
                    <KTSVG
                      path='/media/gori/common/invalid.svg'
                      className='svg-icon-3 text-danger'
                    />
                    <span className='ms-2 text-decoration-underline text-uppercase'>
                      {intl.formatMessage({id: 'VIEW'})}
                    </span>
                  </div>
                </div>
              }
              messageTooltip={row?.original?.hold_reason || row?.original?.rejected_reason}
            />
          )
        },
        visible: isCustomIssuesTab,
      },
      {
        id: 'status-origin',
        Header: intl.formatMessage({id: 'CUSTOM_STATUS'}),
        headerClassName: clsx('fixed-action text-center text-nowrap px-2 min-w-1px right-0'),
        cellClassName: clsx('text-dark fixed-action text-center text-nowrap px-2 right-0'),
        Cell: ({row}: {row: any}) => {
          return <StatusViewOnly status={row.original.release_status} />
        },
        visible: isOrigin && isCustomIssuesTab,
      },
      {
        id: 'status-destination',
        Header: intl.formatMessage({id: 'CUSTOM_STATUS'}),
        headerClassName: clsx('fixed-action text-center text-nowrap px-2 min-w-1px right-0'),
        cellClassName: clsx('text-dark fixed-action text-center text-nowrap px-2 right-0'),
        Cell: ({row}: {row: any}) => {
          return (
            <UpdateStatus
              row={row}
              onChangeCallback={(option) => onSelectStatus(option, row)}
              isUpdating={updatingStatus[row.original.id]}
            />
          )
        },
        visible: isDestination && isCustomIssuesTab,
      },
    ]

    if (isVisibleCheckbox) {
      const canCheck: any = {
        id: TABLE_KEY_COLUMN.CAN_CHECK,
        Header: intl.formatMessage({id: 'ID'}),
        accessor: 'canCheck',
      }
      _columns.splice(1, 0, canCheck)
    }

    return _columns.filter((col) => col && col.visible !== false)
  }, [
    intl,
    isCustomIssuesTab,
    isOrigin,
    isDestination,
    showEditOrderInfo,
    currentUser,
    carriers,
    tableData,
    isHoldOrRejected,
    statesGlobal.settingsConfig,
    updatingStatus,
    onSelectStatus,
    isVisibleCheckbox,
  ])

  const onFinalizeLabels = useCallback(async () => {
    const isConfirm = await confirm({
      title: intl.formatMessage({id: 'FINALIZE_A1_BATCH'}),
    })
    if (!isConfirm) return

    try {
      setLoading((prev) => ({...prev, finalizeLabels: true}))
      const {message}: any = await A1BatchService.finalizeLabels(id)

      if (message) {
        toast.success(message)
        getA1Batch()
      }
    } catch (error: any) {
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    } finally {
      setLoading((prev) => ({...prev, finalizeLabels: false}))
    }
  }, [confirm, getA1Batch, id, intl, isCancel])

  useEffect(() => {
    const statusObj = {}

    tableData.forEach((row) => {
      statusObj[row.id] = false
    })
    setUpdatingStatus(statusObj)
  }, [tableData])

  const handleConfirmPickupDate = useCallback(() => {
    setShowModal((prev) => ({...prev, confirmPickUpdate: true}))
  }, [])

  const handleMarkAsPickedUp = useCallback(
    async ({hasPass = false}) => {
      if (!hasPass) {
        setDataModal((prev) => ({
          ...prev,
          confirmJourneyStatus: {
            title: intl.formatMessage({id: 'MARK_AS_PICKED_UP'}),
            callback: () => handleMarkAsPickedUp({hasPass: true}),
          },
        }))
        return setShowModal((prev) => ({...prev, confirmJourneyStatus: true}))
      }

      try {
        setLoading((prev) => ({...prev, markAsPickedUp: true}))
        const {message}: any = await A1BatchService.markAsPickedUp(id, {
          cancelToken: newCancelToken(),
        })
        if (message) {
          toast.success(message)
          getA1Batch()
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setLoading((prev) => ({...prev, markAsPickedUp: false}))
      }
    },
    [getA1Batch, id, intl, isCancel, newCancelToken]
  )

  const handleExportManifest = useCallback(
    async (extension: FileExtension) => {
      try {
        setLoading((prev) => ({...prev, exportManifest: true}))
        const response: any = await A1BatchService.exportManifest(id, extension, {
          cancelToken: newCancelToken(),
        })
        if (response?.blob) {
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(response.blob)
          link.download = response.fileName || `${a1Batch?.name}_A1 Export.${extension}`
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(link.href)
        }
      } catch (error: any) {
        if (isCancel(error)) return
        const blob = error?.response?.data?.blob ?? error?.response?.data
        if (blob instanceof Blob) {
          try {
            const json = JSON.parse(await blob.text())
            json?.message && toast.error(json.message)
          } catch {
            // ignore non-JSON error body
          }
        }
      } finally {
        setLoading((prev) => ({...prev, exportManifest: false}))
      }
    },
    [a1Batch?.name, id, isCancel, newCancelToken]
  )

  const handleNotifyDestination = useCallback(
    async ({hasPass = false}) => {
      if (!a1Batch?.document_count && !a1Batch?.is_yamato) {
        return toast.error(
          intl.formatMessage({
            id: 'NOTIFY_DESTINATION_PARTNER_T86_REQUIRED',
          })
        )
      }
      if (!hasPass) {
        setDataModal((prev) => ({
          ...prev,
          confirmJourneyStatus: {
            title: intl.formatMessage({id: 'NOTIFY_DESTINATION_PARTNER'}),
            callback: () => handleNotifyDestination({hasPass: true}),
          },
        }))
        return setShowModal((prev) => ({...prev, confirmJourneyStatus: true}))
      }

      try {
        setLoading((prev) => ({...prev, notifyDestination: true}))
        const {message}: any = await A1BatchService.notificationDestination(id, {
          cancelToken: newCancelToken(),
        })
        if (message) {
          toast.success(message)
          getA1Batch()
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setLoading((prev) => ({...prev, notifyDestination: false}))
      }
    },
    [a1Batch?.document_count, getA1Batch, id, intl, isCancel, newCancelToken, a1Batch?.is_yamato]
  )

  const handleMassAsShipped = useCallback(
    async ({hasPass = false}) => {
      if (!hasPass) {
        setDataModal((prev) => ({
          ...prev,
          confirmJourneyStatus: {
            title: intl.formatMessage({id: 'MARK_AS_SHIPPED'}),
            callback: () => handleMassAsShipped({hasPass: true}),
          },
        }))
        return setShowModal((prev) => ({...prev, confirmJourneyStatus: true}))
      }

      try {
        setLoading((prev) => ({...prev, massAsShipped: true}))
        const {message}: any = await A1BatchService.markAsShipped(id, {
          cancelToken: newCancelToken(),
        })
        if (message) {
          await getA1Batch()
          toast.success(message)
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setLoading((prev) => ({...prev, massAsShipped: false}))
      }
    },
    [getA1Batch, id, intl, isCancel, newCancelToken]
  )

  const handleSetDropOff = useCallback(
    async ({hasPass = false}) => {
      if (!a1Batch?.dropped_off_ready) {
        return toast.warning(
          intl.formatMessage({
            id: 'ENSURE_EACH_ORDER_IN_THE_RELEASE_STATUS_COLUMN_HAS_AT_LEAST_ONE_STATUS',
          })
        )
      }

      if (!hasPass) {
        setDataModal((prev) => ({
          ...prev,
          confirmJourneyStatus: {
            title: intl.formatMessage({id: 'SET_TO_DROP_OFF'}),
            callback: () => handleSetDropOff({hasPass: true}),
          },
        }))
        return setShowModal((prev) => ({...prev, confirmJourneyStatus: true}))
      }

      try {
        setLoading((prev) => ({...prev, setDropOff: true}))
        const {message}: any = await A1BatchService.setDropOff(id, {
          cancelToken: newCancelToken(),
        })
        if (message) {
          toast.success(message)
          getA1Batch()
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setLoading((prev) => ({...prev, setDropOff: false}))
      }
    },
    [a1Batch?.dropped_off_ready, getA1Batch, id, intl, isCancel, newCancelToken]
  )

  const handleMarkAsDroppedOff = useCallback(
    async ({hasPass = false}) => {
      if (isEmpty(a1Batch?.released_at)) {
        return toast.error(
          intl.formatMessage({
            id: 'ENSURE_AT_LEAST_ONE_RELEASED_STATUS_IN_THE_RELEASE_STATUS_COLUMN',
          })
        )
      }

      if (!hasPass) {
        setDataModal((prev) => ({
          ...prev,
          confirmJourneyStatus: {
            title: intl.formatMessage({id: 'MARK_AS_DROPPED_OFF'}),
            callback: () => handleMarkAsDroppedOff({hasPass: true}),
          },
        }))
        return setShowModal((prev) => ({...prev, confirmJourneyStatus: true}))
      }

      try {
        setLoading((prev) => ({...prev, markAsDroppedOff: true}))
        const {message}: any = await A1BatchService.markAsDroppedOff(id, {
          cancelToken: newCancelToken(),
        })
        if (message) {
          toast.success(message)
          getA1Batch()
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setLoading((prev) => ({...prev, markAsDroppedOff: false}))
      }
    },
    [a1Batch?.released_at, getA1Batch, id, intl, isCancel, newCancelToken]
  )

  const isShowMarkAsDroppedOffDestination = useMemo(
    () =>
      CUSTOMS.value[0] === a1Batch?.journey_status &&
      a1Batch?.destination_tab === 'ready_for_drop_off',
    [CUSTOMS.value, a1Batch?.journey_status, a1Batch?.destination_tab]
  )

  const isShowSetToDropOffDestination = useMemo(
    () =>
      CUSTOMS.value[0] === a1Batch?.journey_status &&
      a1Batch?.mawb &&
      a1Batch?.destination_tab !== 'ready_for_drop_off',
    [CUSTOMS.value, a1Batch?.journey_status, a1Batch?.mawb, a1Batch?.destination_tab]
  )

  const isShowButtonUpdateBatchStatusDestination = useMemo(() => {
    if (isShowSetToDropOffDestination || isShowMarkAsDroppedOffDestination) return false
    const disabledStatus = [TRANSFERRED_TO_LOCAL_CARRIER.value[0], DELIVERY_STATUS.value[0]]

    return !disabledStatus.includes(a1Batch?.journey_status) && a1Batch?.mawb
  }, [
    DELIVERY_STATUS.value,
    TRANSFERRED_TO_LOCAL_CARRIER.value,
    a1Batch?.journey_status,
    a1Batch?.mawb,
    isShowMarkAsDroppedOffDestination,
    isShowSetToDropOffDestination,
  ])

  const handleCloseEditOrderInfo = useCallback(() => {
    setDataModal((prev) => ({...prev, editOrderInfo: {}}))
    setShowModal((prev) => ({...prev, editOrderInfo: false}))
  }, [])

  const handleConfirmJourneyStatus = useCallback(async () => {
    setShowModal((prev) => ({...prev, confirmJourneyStatus: false}))
    await dataModal.confirmJourneyStatus.callback()
    await setDataModal((prev) => ({
      ...prev,
      confirmJourneyStatus: {
        status: null,
        callback: null,
      },
    }))
  }, [dataModal.confirmJourneyStatus])

  const handleCloseConfirmJourneyStatus = useCallback(() => {
    setDataModal((prev) => ({
      ...prev,
      confirmJourneyStatus: {
        title: null,
        message: null,
        status: null,
        callback: null,
      },
    }))
    setShowModal((prev) => ({...prev, confirmJourneyStatus: false}))
  }, [])

  const handleTicketForum = useCallback(() => {
    const activeTabName = Object.entries(a1Batch?.ticket_unread_count ?? {}).find(
      (item) => (item as [string, number])[1] > 0
    )?.[0]
    searchParams.set('page', DEFAULT_PAGE)
    if (activeTabName) {
      searchParams.set('recipient', activeTabName)
    }
    searchParams.delete('page_size')
    searchParams.set('modal', 'true')
    setSearchParams(searchParams)
    document.body.style.overflow = 'hidden'
    setTicketForum((prev) => ({...prev, showList: true}))
  }, [searchParams, setSearchParams, setTicketForum, a1Batch?.ticket_unread_count])

  const refreshData = useCallback(() => {
    setTable()
    getA1Batch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!a1Batch?.id) return

    if (get(state, 'ticketModal')) {
      handleTicketForum()
    }
  }, [handleTicketForum, state, a1Batch?.id])

  const handleCloseTicketForum = () => {
    getA1Batch()
    document.body.style.overflow = 'auto'
    setTicketForum((prev) => ({...prev, showList: false}))
  }

  // NOTE: Only for Yamato
  const isShowExportManifest = useMemo(() => {
    const allowedStatus = [
      PICK_UP_DROP_OFF.value[2],
      AIR_DELIVERY.value[0],
      TRANSFERRED_TO_LOCAL_CARRIER.value[0],
      CUSTOMS.value[0],
      DELIVERY_STATUS.value[0],
    ]

    return !!(isOrigin && a1Batch?.is_yamato && allowedStatus.includes(a1Batch?.journey_status))
  }, [
    PICK_UP_DROP_OFF.value,
    AIR_DELIVERY.value,
    TRANSFERRED_TO_LOCAL_CARRIER.value,
    CUSTOMS.value,
    DELIVERY_STATUS.value,
    isOrigin,
    a1Batch?.is_yamato,
    a1Batch?.journey_status,
  ])
  const orderIdsByStatus = useCallback(
    (status: string) => {
      return reduce(
        tableDataAll,
        (
          result: number[],
          {sb_order_id, status: orderStatus}: {sb_order_id: number; status: string}
        ) => {
          if (orderStatus === status) {
            result.push(sb_order_id)
          }
          return result
        },
        [] as number[]
      )
    },
    [tableDataAll]
  )

  const orderIds: {createAllLabel: number[]; printAllLabel: number[]} = useMemo(() => {
    return {
      createAllLabel: orderIdsByStatus(OrderStatus.OPEN),
      printAllLabel: orderIdsByStatus(OrderStatus.SHIPPED),
    }
  }, [orderIdsByStatus])

  return loading.first || isEmpty(a1Batch) ? (
    <SkeletonA1BatchDetail />
  ) : (
    <>
      {ConfirmModal}
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      {showModal.trackingNumbersDetail && (
        <TrackingNumbersDetail
          show={showModal.trackingNumbersDetail}
          data={dataModal.trackingNumbersDetail}
          handleClose={() => setShowModal((prev) => ({...prev, trackingNumbersDetail: false}))}
        />
      )}
      {showModal.rename && (
        <A1BatchRenameModal
          show={showModal.rename}
          handleClose={() => setShowModal((prev) => ({...prev, rename: false}))}
        />
      )}
      {showModal.journeyInfo && (
        <JourneyInformationModal
          show={showModal.journeyInfo}
          handleClose={() => setShowModal((prev) => ({...prev, journeyInfo: false}))}
          handleSuccess={() => {
            getA1Batch()
            setTable()
            setShowModal((prev) => ({...prev, journeyInfo: false}))
          }}
        />
      )}
      {showModal.confirmPickUpdate && (
        <ConfirmA1BatchModal
          show={showModal.confirmPickUpdate}
          handleClose={() => setShowModal((prev) => ({...prev, confirmPickUpdate: false}))}
          handleSuccess={() => {
            getA1Batch()
            setShowModal((prev) => ({...prev, confirmPickUpdate: false}))
          }}
        />
      )}
      {showModal.t86DocumentModal && (
        <T86DocumentModal
          show={showModal.t86DocumentModal}
          handleClose={() => setShowModal((prev) => ({...prev, t86DocumentModal: false}))}
        />
      )}
      {showModal.editOrderInfo && (
        <EditOrderInfo
          show={showModal.editOrderInfo}
          data={dataModal.editOrderInfo}
          handleClose={handleCloseEditOrderInfo}
        />
      )}
      {showModal.confirmJourneyStatus && (
        <ConfirmActionModal
          show={showModal.confirmJourneyStatus}
          classNameDialog='mw-750px'
          title={
            dataModal?.confirmJourneyStatus?.title ||
            intl.formatMessage({id: 'UPDATE_JOURNEY_STATUS'})
          }
          message={
            dataModal?.confirmJourneyStatus?.message || (
              <span className='fw-bold text-center'>
                <p>{intl.formatMessage({id: 'ARE_YOU_SURE_YOU_WANT_TO_PROCEED'})}</p>
                <p>
                  {intl.formatMessage({
                    id: 'THIS_ACTION_CANNOT_BE_UNDONE_PLEASE_REVIEW_CAREFULLY_BEFORE_PROCEEDING',
                  })}
                </p>
              </span>
            )
          }
          messagePrimary={intl.formatMessage({id: 'CONFIRM'})}
          messageSecondary={intl.formatMessage({id: 'CANCEL'})}
          handlePrimaryAction={handleConfirmJourneyStatus}
          handleSecondaryAction={handleCloseConfirmJourneyStatus}
          handleClose={handleCloseConfirmJourneyStatus}
        />
      )}
      <TicketForumModal show={ticketForum?.showList} handleClose={handleCloseTicketForum} />
      {showModal.massUpdate && (
        <MassUpdateModal
          show={showModal.massUpdate}
          handleClose={() => {
            const targetRowId = dataModal?.massUpdate?.rowId

            if (targetRowId) {
              const targetUpdatingState = updatingStatus[targetRowId]

              setUpdatingStatus((prev) => ({
                ...prev,
                [targetRowId]: !targetUpdatingState,
              }))
            }
            setShowModal((prev) => ({...prev, massUpdate: false}))
          }}
          data={dataModal.massUpdate}
          reloadTable={() => {
            setTable()
          }}
        />
      )}
      {showModal.reason && (
        <ReasonModal
          show={showModal.reason}
          handleClose={() => setShowModal((prev) => ({...prev, reason: false}))}
          data={dataModal.reasonData}
        />
      )}
      {showModal.removedOrdersHistory && (
        <RemovedOrdersHistoryModal
          show={showModal.removedOrdersHistory}
          batchId={id}
          handleClose={() => setShowModal((prev) => ({...prev, removedOrdersHistory: false}))}
        />
      )}
      <PageTitle>{intl.formatMessage({id: 'UPLOAD_A1'})}</PageTitle>
      <div className={clsx({'cursor-no-drop': loading.getA1Batch})}>
        <div className={clsx({'pe-none opacity-75': loading.getA1Batch})}>
          <div className='d-flex flex-wrap justify-content-between mb-4'>
            <div className='d-flex'>
              <ButtonBack className={'bg-page'} config={{url: '/a1-batches'}} />
              {a1Batch?.date && (
                <div className='fw-bolder'>
                  <span className='fs-3'>{a1Batch?.name}</span>
                  <div className='fs-6 mt-2 fw-bold'>
                    <span className='me-1'>
                      {convertUserTimeZone(a1Batch?.date, currentUser, FORMAT_DATE.DATE)}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className='d-flex ms-auto'>
              <div>
                <div
                  className='ms-6 me-4 bg-white rounded-2 btn bg-hover-light'
                  onClick={handleTicketForum}
                >
                  <KTSVG
                    path={
                      a1Batch?.ticket_comment_unread
                        ? '/media/gori/a1/unread-message.svg'
                        : '/media/gori/a1/message.svg'
                    }
                    small={false}
                    style={{color: baseColor}}
                  />
                </div>
              </div>
              {isOrigin && (
                <div className='d-flex gap-4'>
                  {isVisibleCreateLabelButton && (
                    <CreateLabelButton
                      batchId={Number(id)}
                      onSuccessCallback={refreshData}
                      orderIds={orderIds.createAllLabel}
                      isCreateAllLabels
                    />
                  )}
                  {isVisiblePrintLabelsButton && (
                    <PrintLabelButton
                      batchId={Number(id)}
                      onSuccessCallback={refreshData}
                      onErrorCallback={(error) => setValidationErrors(error?.response)}
                      orderIds={orderIds.printAllLabel}
                    />
                  )}
                  {isVisibleFinalizeBatchButton && (
                    <Button
                      className='btn btn-primary'
                      label={intl.formatMessage({id: 'FINALIZE_A1_BATCH'})}
                      event={onFinalizeLabels}
                      loading={loading.finalizeLabels}
                    />
                  )}

                  {READY.value.includes(a1Batch?.journey_status) && (
                    <div>
                      <Button
                        className='btn btn-primary'
                        label={intl.formatMessage({id: 'CONFIRM_PICKUP_DATE'})}
                        event={handleConfirmPickupDate}
                      />
                    </div>
                  )}
                  {PICK_UP_DROP_OFF.value[0] === a1Batch?.journey_status && (
                    <Button
                      className='btn btn-primary'
                      label={intl.formatMessage({id: 'MARK_AS_PICKED_UP'})}
                      loading={loading.markAsPickedUp}
                      disabled={loading.markAsPickedUp}
                      event={handleMarkAsPickedUp}
                    />
                  )}
                  {PICK_UP_DROP_OFF.value[1] === a1Batch?.journey_status && (
                    <Button
                      className='btn btn-primary'
                      label={intl.formatMessage({id: 'UPDATE_BATCH_STATUS'})}
                      event={() => setShowModal((prev) => ({...prev, journeyInfo: true}))}
                    />
                  )}
                  {isShowExportManifest && (
                    <div>
                      <DropdownButton
                        fitContent
                        className={clsx(
                          'btn border border-2 h-43px text-primary border-primary bg-white bg-hover-primary-light',
                          {'pe-none opacity-75': loading.exportManifest}
                        )}
                        title={intl.formatMessage({id: 'EXPORT_DATA'})}
                        loading={loading.exportManifest}
                        icon={<i className='bi bi-caret-down-fill ms-2 text-primary' />}
                        list={EXCEL_EXPORT_OPTIONS.map((option) => ({
                          label: option.label,
                          value: option.value,
                          action: () => handleExportManifest(option.value as FileExtension),
                        }))}
                      />
                    </div>
                  )}

                  {PICK_UP_DROP_OFF.value[2] === a1Batch?.journey_status && (
                    <Button
                      className='btn btn-primary'
                      label={intl.formatMessage({id: 'NOTIFY_DESTINATION_PARTNER'})}
                      loading={loading.notifyDestination}
                      disabled={loading.notifyDestination}
                      event={handleNotifyDestination}
                    />
                  )}
                  {A1Helpers.checkJourneyStatus({
                    statusCurrent: a1Batch?.journey_status,
                    statusCheck: AIR_DELIVERY.value[0],
                  }) &&
                    a1Batch?.origin_tab === A1_CONSTANTS.A1_TABS.ORIGIN.PICKED_UP.VALUE && (
                      <Button
                        className='btn btn-primary'
                        label={intl.formatMessage({id: 'MARK_AS_SHIPPED'})}
                        loading={loading.massAsShipped}
                        disabled={loading.massAsShipped}
                        event={handleMassAsShipped}
                      />
                    )}
                </div>
              )}
              {isDestination && (
                <div>
                  {isShowButtonUpdateBatchStatusDestination && (
                    <Button
                      className='btn btn-primary'
                      label={intl.formatMessage({id: 'UPDATE_BATCH_STATUS'})}
                      event={() => setShowModal((prev) => ({...prev, journeyInfo: true}))}
                    />
                  )}
                  {isShowSetToDropOffDestination && (
                    <Button
                      className={clsx('btn btn-primary', {
                        'opacity-75 cursor-no-drop': !a1Batch?.dropped_off_ready,
                      })}
                      label={intl.formatMessage({id: 'SET_TO_DROP_OFF'})}
                      event={handleSetDropOff}
                      loading={loading.setDropOff}
                      disabled={loading.setDropOff}
                    />
                  )}
                  {isShowMarkAsDroppedOffDestination && (
                    <Button
                      className={clsx('btn btn-primary', {
                        'opacity-75 cursor-no-drop': !a1Batch?.released_at,
                      })}
                      label={intl.formatMessage({id: 'MARK_AS_DROPPED_OFF'})}
                      event={handleMarkAsDroppedOff}
                      loading={loading.markAsDroppedOff}
                      disabled={loading.markAsDroppedOff}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className='d-flex flex-wrap mb-10'>
            <div className='col-12 col-xxl-6'>
              <JourneyInfoCard
                handleEditConfirmPickupDate={() =>
                  setShowModal((prev) => ({...prev, confirmPickUpdate: true}))
                }
                handleEditJourneyInfo={() => {
                  setShowModal((prev) => ({...prev, journeyInfo: true}))
                }}
                handleOpenDocument={() => {
                  setShowModal((prev) => ({...prev, t86DocumentModal: true}))
                }}
              />
            </div>
            <div className='col-12 col-xxl-6'>
              <JourneyStatusCard />
            </div>
          </div>
          <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
            <TableTabs
              disableTabAll
              dataTabs={dataTabs}
              keyCheckActive='order_tab'
              childrenTab={
                // Removed Orders History is Origin-only for now; Destination gets 403 from the API.
                isOrigin &&
                !!a1Batch?.is_yamato && (
                  <div
                    className='btn bg-white bg-hover-light-blue border border-2 text-blue border-blue cursor-pointer fw-bolder d-flex align-items-center gap-2 btn-view-log-history'
                    onClick={() => setShowModal((prev) => ({...prev, removedOrdersHistory: true}))}
                  >
                    {intl.formatMessage({id: 'VIEW_LOG_HISTORY'})}
                    <span className='btn-view-log-history__badge'>
                      {a1Batch?.removed_orders_count ?? 0}
                    </span>
                  </div>
                )
              }
              children={
                <TableWrapper className='rounded-top-left-0-dt'>
                  <TableHeader className='min-h-1px d-flex flex-column min-h-1'>
                    <A1BatchesDetailFilter
                      isVisibleCheckbox={isVisibleCheckbox}
                      isVisiblePrintLabel={isVisiblePrintLabelsButton}
                      isVisibleCreateLabel={isVisibleCreateLabelButton}
                      selectedList={{
                        selected: selectedListId,
                        selectedMaster: selectedMaster,
                        total: tableDataAll,
                      }}
                      reloadTable={() => {
                        getA1Batch()
                        setTable()
                      }}
                    />
                  </TableHeader>
                  <TableBody height={'calc(100vh - 300px)'}>
                    <Table
                      columns={columns}
                      data={tableData}
                      pagination={pagination}
                      tbodyClass='text-gray-600 fw-bold'
                      usePagination
                      useCheckBox
                      callBackSetData={(selected) => {
                        setSelectedMaster(selected?.length === pagination?.page_size)
                        setSelectedListId(selected)
                      }}
                    />
                  </TableBody>
                </TableWrapper>
              }
            />
          </CSSTransition>
        </div>
      </div>
    </>
  )
}

export {A1BatchesDetailPage}
